import { WarningCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import type { DealerContact } from '@entities/handover';
import { Badge } from '@shared/ui';

import styles from './Handover.module.css';

interface SellerContactCardProps {
  contact: DealerContact | null;
  /** Название заказа — контекст «по какому запросу этот автохаус». */
  orderTitle: string | null;
}

/**
 * Контакты автохауса — первое, что нужно на шаге 7 (FE-260, MVP_PRD §39.1).
 *
 * Три правила блока:
 *
 * * **только то, что есть в каталоге и в переписке**: имя, город и адрес, по
 *   которому автохаус сам предложил обращаться. Телефона здесь нет — система
 *   его не собирает, а вывести по домену значило бы выдумать (§2);
 * * **ни одного слова об автохаусе как о продавце.** «Надёжный», «проверенный»,
 *   «безопасная сделка» запрещены: CarViQ.DE продавцов не проверяет и агрегата
 *   -вердикта не имеет (§37.1);
 * * **недействующий контакт назван прямо.** Автохаус мог отказаться от
 *   переписки (§23.8) — и тогда пользователь должен знать, что письмо от Karl
 *   туда больше не уйдёт, а не выяснять это молчанием.
 */
export function SellerContactCard({ contact, orderTitle }: SellerContactCardProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby="handover-contact">
      <h2 id="handover-contact" className={styles.title}>
        {t('handover.contact.title')}
      </h2>
      <p className={styles.subtitle}>{t('handover.contact.subtitle')}</p>

      {contact === null ? (
        <p className={styles.note}>{t('handover.contact.missing')}</p>
      ) : (
        <>
          <dl className={styles.facts}>
            {orderTitle === null ? null : (
              <div>
                <dt>{t('handover.contact.order')}</dt>
                <dd>{orderTitle}</dd>
              </div>
            )}
            <div>
              <dt>{t('handover.contact.name')}</dt>
              <dd>{contact.name ?? t('common.noData')}</dd>
            </div>
            <div>
              <dt>{t('handover.contact.city')}</dt>
              <dd>{contact.city ?? t('common.noData')}</dd>
            </div>
            <div>
              <dt>{t('handover.contact.address')}</dt>
              <dd>
                <a href={`mailto:${contact.address}`}>{contact.address}</a>
              </dd>
            </div>
          </dl>

          {contact.active ? null : (
            <p className={styles.warning}>
              <Badge tone="attention" icon={<WarningCircleIcon weight="bold" />}>
                {t('handover.contact.inactiveBadge')}
              </Badge>{' '}
              <span>{t('handover.contact.inactive')}</span>
            </p>
          )}
        </>
      )}

      {/* Договор пользователь заключает с автохаусом напрямую (§39.2). Это
          стоит рядом с контактами, а не в подвале: именно здесь начинается
          то, в чём CarViQ.DE стороной не является. */}
      <p className={styles.note}>{t('handover.contact.directly')}</p>
    </section>
  );
}
